import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";

import {
  getCoreRowModel,
  SortingState,
  useReactTable
} from "@tanstack/react-table";

import { useGetDatasetListQuery } from "@/api/dataset/useDatasetAPI";
import { useSubscription } from "@/api/subscriptions/useSubscriptionsAPI";
import { useGetUserProfileAPI } from "@/api/userProfile/useUserProfileAPI";
import { UnlockPremiumModal } from "@/components/dataset/datasetExplorer/UnlockPremiumModal";
import AddDatasetDialog from "@/components/datasetManagement/AddDatasetDialog";
import {
  columns,
  DatasetImportActions
} from "@/components/datasetManagement/columns.tsx";
import EmptyState from "@/components/datasetManagement/EmptyState";
import FilterSection from "@/components/datasetManagement/FilterSection";
import LoadingState from "@/components/datasetManagement/LoadingState";
import Pagination from "@/components/datasetManagement/Pagination";
import TableData from "@/components/datasetManagement/TableData";
import UploadDialog from "@/components/datasetManagement/UploadDialog";
import {
  BRING_YOUR_OWN_DATA_MOCKS,
  BRING_YOUR_OWN_DATA_UPLOAD_DROPZONE_TOUR_ID
} from "@/components/onboarding/configs/bring-your-own-data.mocks";
import { Button } from "@/components/ui/button";
import { Dialog, DialogTrigger } from "@/components/ui/dialog";
import { Progress } from "@/components/ui/progress";
import {
  CANCEL_IMPORT_MESSAGE,
  TARGETIN_IMPORT_MESSAGE
} from "@/constants/message";
import { getDisplayErrorMessage } from "@/helpers/handleErrorResponse";
import {
  EModuleName,
  EModulePermission,
  useGetUserPermission
} from "@/hooks/permission/useGetUserPermission";
import useSessionStorage from "@/hooks/store/useSessionStorage";
import { useConfirmationModal } from "@/hooks/ui/modal/useConfirmationModal";
import { EToastType, showToast } from "@/hooks/ui/useToast";
import { useAppFeatures } from "@/hooks/useAppFeatures";
import { useDatasetFilters } from "@/hooks/useDatasetFilters";
import { usePagination } from "@/hooks/usePagination";
import { useParsingStatus } from "@/hooks/useParsingStatus";
import { cn } from "@/lib/utils";
import {
  fetchTargetinDatasetMetadata,
  fetchTargetinDatasets,
  TargetinDataset,
  TargetinImportCancelledError
} from "@/mocks/targetin/dummyTargetinImport";
import { RootState } from "@/store";
import { CountryCode, SpatialAggregationType } from "@/types/common";
import {
  DATASET_SORT_TYPE,
  DatasetExploreItem,
  EAddDatasetSource,
  EDatasetOrigin,
  ETargetinImportStatus,
  TargetinImportJob
} from "@/types/dataset";
import { formatStorageFromKB } from "@/utils/format";

type TABLE_KEY =
  | "name"
  | "country_id"
  | "spatial_aggregation_type"
  | "published"
  | "created_at"
  | "updated_at"
  | "size_kb";

type SortOrder = "ASC" | "DESC";

const SORT_PARSED: Record<TABLE_KEY, DATASET_SORT_TYPE> = {
  name: "name",
  country_id: "country",
  spatial_aggregation_type: "spatial_aggregation_type",
  created_at: "date",
  published: "status",
  updated_at: "updated_at",
  size_kb: "size"
};

// Reverse mapping to convert from DATASET_SORT_TYPE to TABLE_KEY
const SORT_REVERSE: Record<DATASET_SORT_TYPE, TABLE_KEY> = {
  name: "name",
  country: "country_id",
  spatial_aggregation_type: "spatial_aggregation_type",
  date: "created_at",
  status: "published",
  updated_at: "updated_at",
  size: "size_kb"
};

export interface DatasetManagementSessionData {
  searchQuery: string;
  filters: {
    selectedCountry: CountryCode | "all";
    spatialAggregation: SpatialAggregationType | "all";
    published: boolean | "all";
    origin: EDatasetOrigin | "all";
  };
  pageSize: number;
  currentPage: number;
  sortBy: DATASET_SORT_TYPE;
  sortOrder: SortOrder;
}

const SESSION_STORAGE_KEY = "dataset-management-page";

const DEFAULT_SESSION_DATA: DatasetManagementSessionData = {
  searchQuery: "",
  filters: {
    selectedCountry: "all",
    spatialAggregation: "all",
    published: "all",
    origin: "all"
  },
  pageSize: 10,
  currentPage: 1,
  sortBy: "date",
  sortOrder: "DESC"
};

// Helper function to check if the path is dataset management related
const isDatasetManagementPath = (pathname: string): boolean => {
  const datasetManagementPattern = /\/dataset-management(\/[^/]+)?$/;
  return datasetManagementPattern.test(pathname);
};

// Helper function to convert SortingState to sort values
const getSortValues = (
  sorting: SortingState
): { sortBy: DATASET_SORT_TYPE; sortOrder: SortOrder } => {
  const sortBy = sorting.length
    ? (SORT_PARSED[sorting[0].id as TABLE_KEY] as DATASET_SORT_TYPE | undefined)
    : undefined;

  // Nothing sorted, or a column whose sort control the list API has no key for
  // (Source, Total Row). Falls back to the default: passed through, the key
  // would reach the query as undefined and wipe sortBy in session storage.
  if (!sortBy) {
    return {
      sortBy: "date",
      sortOrder: "DESC"
    };
  }

  return { sortBy, sortOrder: sorting[0].desc ? "DESC" : "ASC" };
};

// Helper function to convert session data to SortingState
const getInitialSorting = (
  sessionData: DatasetManagementSessionData | null
): SortingState => {
  if (!sessionData?.sortBy) {
    return [];
  }
  const tableKey = SORT_REVERSE[sessionData.sortBy];
  return [
    {
      id: tableKey,
      desc: sessionData.sortOrder === "DESC"
    }
  ];
};

export default function DatasetManagementPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { showConfirmation } = useConfirmationModal();
  const { isStorageLimitEnabled, isTargetinIntegrationEnabled } =
    useAppFeatures();
  const isBringYourOwnDataOnboarding = useSelector(
    (state: RootState) => state.onboarding.active
  );
  const activeStepTourId = useSelector(
    (state: RootState) => state.onboarding.activeStepTourId
  );

  // During the tour the upload dialog belongs to the dropzone step only. Tie its
  // visibility to that step so navigating away (Prev/Next) closes it — the open
  // flag alone is one-shot and would otherwise linger on other steps.
  const isOnboardingUploadStep =
    isBringYourOwnDataOnboarding &&
    activeStepTourId === BRING_YOUR_OWN_DATA_UPLOAD_DROPZONE_TOUR_ID;

  const [isStorageLimitDialogOpen, setIsStorageLimitDialogOpen] =
    useState(false);

  const { sessionValue: sessionStorageData, setSessionItem } =
    useSessionStorage<DatasetManagementSessionData>(
      SESSION_STORAGE_KEY,
      DEFAULT_SESSION_DATA
    );

  // Initialize state from sessionStorage or use defaults
  const {
    filters,
    setFilters: originalSetFilters,
    searchQuery,
    setSearchQuery: originalSetSearchQuery,
    debouncedSearchQuery,
    isDefaultFilters
  } = useDatasetFilters({
    // Merged over the defaults so an entry stored before a filter existed does
    // not leave it undefined.
    initialFilters: {
      ...DEFAULT_SESSION_DATA.filters,
      ...sessionStorageData?.filters
    },
    initialSearchQuery:
      sessionStorageData?.searchQuery ?? DEFAULT_SESSION_DATA.searchQuery
  });

  const {
    pageSize,
    setPageSize: originalSetPageSize,
    currentPage,
    setCurrentPage: originalSetCurrentPage
  } = usePagination({
    initialPageSize:
      sessionStorageData?.pageSize ?? DEFAULT_SESSION_DATA.pageSize,
    initialPage:
      sessionStorageData?.currentPage ?? DEFAULT_SESSION_DATA.currentPage
  });

  const [isUploadDialogOpen, setIsUploadDialogOpen] = useState<boolean>(false);
  const [isAddDatasetDialogOpen, setIsAddDatasetDialogOpen] =
    useState<boolean>(false);

  // The tour drives the upload dialog through its own steps, so the picker stays
  // out of the way.
  const isAddDatasetPickerEnabled =
    isTargetinIntegrationEnabled && !isBringYourOwnDataOnboarding;

  const openAddDatasetDialog = useCallback(
    () => setIsAddDatasetDialogOpen(true),
    []
  );

  /** Datasets being pulled from Targetin, keyed by id. */
  const [importJobs, setImportJobs] = useState<
    Record<string, TargetinImportJob>
  >({});
  /** True while asking Targetin what it has. */
  const [isDiscoveringTargetinDatasets, setIsDiscoveringTargetinDatasets] =
    useState(false);
  const importControllers = useRef(new Map<string, AbortController>());

  /**
   * Announces one dataset's outcome. Stacks, so an earlier dataset's result is
   * not wiped by the next; its own id keeps each from replacing another.
   */
  const notifyImportSettled = useCallback(
    (datasetName: string, isSuccess: boolean) => {
      const toastId = `targetin-import-${datasetName}`;

      showToast({
        id: toastId,
        type: isSuccess ? EToastType.SUCCESS : EToastType.ERROR,
        autoClose: 2000,
        pauseOnHover: true,
        stack: true,
        title: isSuccess
          ? TARGETIN_IMPORT_MESSAGE.SUCCESS_TITLE
          : TARGETIN_IMPORT_MESSAGE.FAILED_TITLE,
        // Only the CTA goes beyond a plain toast; the rest is title/message.
        message: (
          <>
            <span>
              {isSuccess
                ? TARGETIN_IMPORT_MESSAGE.SUCCESS_DESCRIPTION(datasetName)
                : TARGETIN_IMPORT_MESSAGE.FAILED_DESCRIPTION(datasetName)}
            </span>
            <button
              type="button"
              data-testid="targetin-import-toast-view"
              className="block mt-1 font-semibold text-left text-royal-blue hover:underline focus:outline-none"
              onClick={() => {
                // Left open, it would sit over the page just navigated to.
                toast.dismiss(toastId);
                navigate("/dataset-management");
              }}
            >
              {TARGETIN_IMPORT_MESSAGE.VIEW}
            </button>
          </>
        )
      });
    },
    [navigate]
  );

  const updateImportJob = useCallback(
    (datasetId: string, patch: Partial<TargetinImportJob>) =>
      setImportJobs((current) =>
        current[datasetId]
          ? { ...current, [datasetId]: { ...current[datasetId], ...patch } }
          : current
      ),
    []
  );

  /**
   * Pulls one dataset's metadata. Each gets its own controller, so cancelling one
   * leaves the others running.
   */
  const pullDatasetMetadata = useCallback(
    (dataset: TargetinDataset) => {
      const controller = new AbortController();
      importControllers.current.set(dataset.id, controller);

      fetchTargetinDatasetMetadata(dataset.id, controller.signal)
        .then(({ totalRows }) => {
          // Cancelling drops the controller along with the row, so a result that
          // still arrives belongs to a dataset the user let go of.
          if (!importControllers.current.has(dataset.id)) return;

          updateImportJob(dataset.id, {
            status: ETargetinImportStatus.SUCCESS,
            totalRows
          });
          notifyImportSettled(dataset.name, true);
        })
        .catch((error) => {
          // Not a failure: its row is already gone and the user asked for it.
          if (
            error instanceof TargetinImportCancelledError ||
            !importControllers.current.has(dataset.id)
          )
            return;

          updateImportJob(dataset.id, {
            status: ETargetinImportStatus.FAILED
          });
          notifyImportSettled(dataset.name, false);
        })
        .finally(() => importControllers.current.delete(dataset.id));
    },
    [notifyImportSettled, updateImportJob]
  );

  /** Starts the pull: dataset list first, then each dataset's metadata. */
  const startTargetinImport = useCallback(async () => {
    setIsDiscoveringTargetinDatasets(true);

    const datasets = await fetchTargetinDatasets();

    setIsDiscoveringTargetinDatasets(false);
    setImportJobs(
      Object.fromEntries(
        datasets.map((dataset) => [
          dataset.id,
          { ...dataset, status: ETargetinImportStatus.LOADING, totalRows: 0 }
        ])
      )
    );

    datasets.forEach(pullDatasetMetadata);
  }, [pullDatasetMetadata]);

  const retryImport = useCallback(
    (datasetId: string) => {
      const job = importJobs[datasetId];
      if (!job) return;

      updateImportJob(datasetId, {
        status: ETargetinImportStatus.LOADING,
        totalRows: 0
      });
      pullDatasetMetadata(job);
    },
    [importJobs, pullDatasetMetadata, updateImportJob]
  );

  /** Asks first, then stops this dataset's import and drops its row. */
  const requestCancelImport = useCallback(
    (datasetId: string) => {
      if (!importJobs[datasetId]) return;

      showConfirmation({
        title: CANCEL_IMPORT_MESSAGE.TITLE,
        message: CANCEL_IMPORT_MESSAGE.DESCRIPTION,
        cancelLabel: CANCEL_IMPORT_MESSAGE.KEEP,
        confirmLabel: CANCEL_IMPORT_MESSAGE.CONFIRM,
        onConfirm: () => {
          importControllers.current.get(datasetId)?.abort();
          importControllers.current.delete(datasetId);
          setImportJobs(({ [datasetId]: _cancelled, ...rest }) => rest);
        }
      });
    },
    [importJobs, showConfirmation]
  );

  const importActions = useMemo<DatasetImportActions>(
    () => ({
      getStatus: (datasetId: string) => importJobs[datasetId]?.status,
      onCancel: requestCancelImport,
      onRetry: retryImport
    }),
    [importJobs, requestCancelImport, retryImport]
  );

  const {
    isParsingActive,
    setIsParsingActive,
    setOperationId,
    setIsKmlUpload
  } = useParsingStatus({});
  const { data: subscriptions } = useSubscription({
    refetchOnMount: "always"
  });

  // Initialize sorting from sessionStorage
  const [sorting, setSorting] = useState<SortingState>(() =>
    getInitialSorting(sessionStorageData)
  );

  // Get current sort values
  const { sortBy, sortOrder } = getSortValues(sorting);

  // Clear session storage when navigating away from dataset-management pages
  useEffect(() => {
    return () => {
      if (!isDatasetManagementPath(window.location.pathname)) {
        sessionStorage.removeItem(SESSION_STORAGE_KEY);
      }
    };
  }, [location.pathname]);

  // Wrapped setters that also update session storage using a functional update
  const setSearchQuery = useCallback(
    (value: string | ((prev: string) => string)) => {
      const resolvedValue =
        typeof value === "function" ? value(searchQuery) : value;
      originalSetSearchQuery(resolvedValue);
      originalSetCurrentPage(1); // Reset to first page on search query change
      setSessionItem({
        ...sessionStorageData,
        searchQuery: resolvedValue,
        currentPage: 1
      });
    },
    [
      originalSetCurrentPage,
      originalSetSearchQuery,
      searchQuery,
      sessionStorageData,
      setSessionItem
    ]
  );

  const setFilters = useCallback(
    (value: typeof filters | ((prev: typeof filters) => typeof filters)) => {
      const resolvedFilters =
        typeof value === "function" ? value(filters) : value;
      originalSetFilters(resolvedFilters);
      originalSetCurrentPage(1); // Reset to first page on filter change
      setSessionItem({
        ...sessionStorageData,
        filters: {
          selectedCountry:
            resolvedFilters.selectedCountry ??
            sessionStorageData.filters.selectedCountry,
          spatialAggregation:
            resolvedFilters.spatialAggregation ??
            sessionStorageData.filters.spatialAggregation,
          published:
            resolvedFilters.published ?? sessionStorageData.filters.published,
          origin: resolvedFilters.origin ?? sessionStorageData.filters.origin
        },
        currentPage: 1
      });
    },
    [
      filters,
      originalSetFilters,
      originalSetCurrentPage,
      setSessionItem,
      sessionStorageData
    ]
  );

  const setPageSize = useCallback(
    (value: number | ((prev: number) => number)) => {
      const resolvedValue =
        typeof value === "function" ? value(pageSize) : value;
      originalSetPageSize(resolvedValue);
      setSessionItem({
        ...sessionStorageData,
        pageSize: resolvedValue
      });
    },
    [pageSize, originalSetPageSize, setSessionItem, sessionStorageData]
  );

  const setCurrentPage = useCallback(
    (value: number | ((prev: number) => number)) => {
      const resolvedValue =
        typeof value === "function" ? value(currentPage) : value;
      originalSetCurrentPage(resolvedValue);

      setSessionItem({
        ...sessionStorageData,
        currentPage: resolvedValue
      });
    },
    [sessionStorageData, currentPage, originalSetCurrentPage, setSessionItem]
  );

  const handleSortingChange = useCallback(
    (updaterOrValue: SortingState | ((prev: SortingState) => SortingState)) => {
      setSorting((prevSorting) => {
        const newSortingState =
          typeof updaterOrValue === "function"
            ? updaterOrValue(prevSorting)
            : updaterOrValue;
        const { sortBy: newSortBy, sortOrder: newSortOrder } =
          getSortValues(newSortingState);
        setSessionItem({
          ...sessionStorageData,
          sortBy: newSortBy,
          sortOrder: newSortOrder
        });
        return newSortingState;
      });
    },
    [setSessionItem, sessionStorageData]
  );

  const {
    data: datasets,
    isLoading,
    isFetching: isFetchingDatasets,
    error: datasetsListError,
    refetch: refetchDatasets
  } = useGetDatasetListQuery(
    {
      q: debouncedSearchQuery,
      spatial_aggregation_type: filters.spatialAggregation,
      page: currentPage,
      per_page: pageSize,
      source: "user",
      sort_by: sortBy,
      sort: sortOrder,
      ...(filters.published !== "all" && { published: filters.published }),
      ...(filters.selectedCountry !== "all" && {
        country: filters.selectedCountry
      })
    },
    {
      refetchOnMount: "always",
      ...(isBringYourOwnDataOnboarding
        ? {
            meta: {
              isOnboarding: true,
              onboardingMockData: BRING_YOUR_OWN_DATA_MOCKS.emptyDatasetList
            }
          }
        : {})
    }
  );

  useEffect(() => {
    if (datasetsListError) {
      showToast({
        message: getDisplayErrorMessage(datasetsListError),
        type: EToastType.ERROR
      });
    }
  }, [datasetsListError]);

  const { data: userProfile } = useGetUserProfileAPI();

  /**
   * Rows for datasets being imported: same columns as real rows, but kept out of
   * the list table so its pagination and sorting stay untouched. Memoized because
   * a fresh array each render resets the page index and re-renders, looping.
   */
  const importRows = useMemo<DatasetExploreItem[]>(
    () =>
      Object.values(importJobs).map((job) => ({
        id: job.id,
        name: job.name,
        // Defaults for an imported dataset; Published only once the pull succeeds.
        short_description: job.name,
        country_id: "id" as CountryCode,
        source: "user",
        origin: EDatasetOrigin.TARGETIN,
        spatial_aggregation_type: "poi",
        published: job.status === ETargetinImportStatus.SUCCESS,
        total_rows: job.totalRows,
        // Blank until stored; formatDate renders an empty string as an empty cell.
        created_at: "",
        updated_at: ""
      })),
    [importJobs]
  );

  /**
   * The list response has no origin yet, so all of it is LI and the Source
   * filter is applied here. Becomes a query parameter once the API supports it.
   */
  const isTargetinOnly = filters.origin === EDatasetOrigin.TARGETIN;
  const isLiOnly = filters.origin === EDatasetOrigin.LI;

  const listRows = useMemo(
    () => (isTargetinOnly ? [] : (datasets?.data ?? [])),
    [datasets?.data, isTargetinOnly]
  );

  const visibleImportRows = useMemo(
    () => (isLiOnly ? [] : importRows),
    [isLiOnly, importRows]
  );

  const importColumns = useMemo(
    () =>
      columns(
        userProfile?.timezone,
        importActions,
        isTargetinIntegrationEnabled
      ),
    [userProfile?.timezone, importActions, isTargetinIntegrationEnabled]
  );

  const importTable = useReactTable({
    data: visibleImportRows,
    columns: importColumns,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true
  });

  // Table configuration
  const table = useReactTable({
    data: listRows,
    columns: columns(
      userProfile?.timezone,
      undefined,
      isTargetinIntegrationEnabled
    ),
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    manualSorting: true,
    pageCount:
      datasets && !isTargetinOnly ? datasets.meta.pagination.total_pages : 0,
    state: {
      sorting
    },
    onSortingChange: handleSortingChange
  });

  const handleOpenChangeDialog = (open: boolean) => {
    // While the tour runs it fully owns the dialog via the active step. Ignore
    // Radix's own open/close requests so no stale open flag leaks out when the
    // tour ends (skip/finish) and re-surfaces the dialog.
    if (isBringYourOwnDataOnboarding) return;
    setIsUploadDialogOpen(open);
  };

  // During onboarding the dialog is driven purely by the active step (open only
  // on the dropzone step). Outside onboarding it follows the user-driven state.
  // Not derived from isUploadDialogOpen during the tour, so exiting mid-step
  // can't leave a stale open flag behind.
  const isDialogOpen = isBringYourOwnDataOnboarding
    ? isOnboardingUploadStep
    : isUploadDialogOpen;

  // A dataset on its way in keeps the table on screen; the empty state would
  // claim nothing is happening.
  const isNoDataset =
    datasets?.data.length === 0 &&
    isDefaultFilters &&
    importRows.length === 0 &&
    !isDiscoveringTargetinDatasets;

  const userPermissions = useGetUserPermission();
  const isCreateDatasetGranted =
    userPermissions[EModuleName.DATASET_MANAGEMENT][EModulePermission.CREATE];

  const totalQuota =
    (subscriptions?.add_on_kb ?? 0) + (subscriptions?.base_kb ?? 0);
  const usedQuota = subscriptions?.used_kb ?? 0;

  const progress = (usedQuota / totalQuota) * 100;

  const handleAddDatasetConfirm = (source: EAddDatasetSource) => {
    setIsAddDatasetDialogOpen(false);

    // Hands over to the upload flow as it already stands; reworking that flow is
    // a separate requirement.
    if (source === EAddDatasetSource.UPLOAD) {
      setIsUploadDialogOpen(true);
      return;
    }

    startTargetinImport();
  };

  // Same button either way; only how it opens differs.
  const addDatasetButton = (
    <Button
      variant="default"
      className={cn(
        "!py-[0.563rem] !px-6 !rounded-xl w-[11.25rem]",
        !isStorageLimitEnabled && "ml-auto"
      )}
      disabled={progress === 100}
      {...(isAddDatasetPickerEnabled && {
        "data-testid": "header-add-dataset-button",
        onClick: openAddDatasetDialog
      })}
    >
      Add Dataset
    </Button>
  );

  // Show parsing state if active
  if (isParsingActive) {
    return <LoadingState message="Loading..." testId="parsing-state" />;
  }

  return (
    <Dialog
      open={isDialogOpen}
      onOpenChange={handleOpenChangeDialog}
      // During the onboarding tour the dialog must be non-modal. A modal Radix
      // dialog disables outside pointer events (body pointer-events: none), which
      // kills the tour tooltip's buttons (Prev/Next/Skip) — they render in a
      // body portal outside the dialog. Non-modal keeps the tour controls live so
      // Prev actually navigates instead of the click being swallowed as a
      // dismiss. Normal (non-tour) usage stays fully modal.
      modal={!isBringYourOwnDataOnboarding}
    >
      <div className="flex flex-col h-full gap-6 p-6">
        <div className="flex flex-row items-center justify-between">
          <h1 className="text-2xl font-bold">Dataset Management</h1>
          <div className="flex items-center flex-1 gap-2 ml-auto">
            {isStorageLimitEnabled && (
              <div className="ml-auto w-[200px] mr-2.5">
                <Progress value={progress} className="h-2" />
                <div className="flex items-center justify-between mt-1">
                  <p className="text-xs text-txtHeading">
                    {formatStorageFromKB(usedQuota)} of{" "}
                    {formatStorageFromKB(totalQuota)}
                  </p>
                  <button
                    className="text-xs font-semibold text-royal-blue hover:underline focus:outline-none"
                    onClick={() => setIsStorageLimitDialogOpen(true)}
                  >
                    Upgrade
                  </button>
                </div>
              </div>
            )}

            {isCreateDatasetGranted &&
              (isAddDatasetPickerEnabled ? (
                addDatasetButton
              ) : (
                <DialogTrigger asChild data-testid="header-upload-button">
                  {addDatasetButton}
                </DialogTrigger>
              ))}
          </div>
        </div>

        <FilterSection
          disabled={isNoDataset && !isLoading}
          filters={filters}
          setFilters={setFilters}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          showSourceFilter={isTargetinIntegrationEnabled}
          // Pulling the latest from Targetin and discovering new datasets comes
          // with the import flow; for now Sync refreshes the list.
          onSync={
            isAddDatasetPickerEnabled ? () => refetchDatasets() : undefined
          }
          isSyncing={isFetchingDatasets}
        />

        {isLoading ? (
          <LoadingState message="Searching..." testId="searching-state" />
        ) : isNoDataset ? (
          <EmptyState
            isCreateDatasetGranted={isCreateDatasetGranted}
            tourId="bring-your-own-data-empty-state"
            buttonTourId="bring-your-own-data-empty-state-upload-button"
            onAddDataset={
              isAddDatasetPickerEnabled ? openAddDatasetDialog : undefined
            }
          />
        ) : (
          <TableData
            table={table}
            // Placeholders while Targetin is asked what it has: the count is not
            // known yet, so this matches the three the design draws.
            skeletonRowCount={isDiscoveringTargetinDatasets ? 3 : 0}
            pendingRows={importTable.getRowModel().rows}
          />
        )}

        {!(isLoading || isNoDataset) && (
          <Pagination
            table={table}
            pageSize={pageSize}
            setPageSize={setPageSize}
            setCurrentPage={setCurrentPage}
            currentPage={currentPage}
            totalData={
              isTargetinOnly ? 0 : datasets?.meta.pagination.total || 0
            }
          />
        )}

        <UploadDialog
          setOperationId={setOperationId}
          setIsParsingActive={setIsParsingActive}
          setIsKmlUpload={setIsKmlUpload}
          // During the tour the dialog is non-modal and opened programmatically;
          // stop Radix from auto-dismissing it so it doesn't flash then vanish.
          preventAutoDismiss={isBringYourOwnDataOnboarding}
          disableAnimation={isBringYourOwnDataOnboarding}
        />

        {/* Own Dialog root: the page's belongs to the upload flow. */}
        {isAddDatasetPickerEnabled && (
          <Dialog
            open={isAddDatasetDialogOpen}
            onOpenChange={setIsAddDatasetDialogOpen}
          >
            {isAddDatasetDialogOpen && (
              <AddDatasetDialog
                onCancel={() => setIsAddDatasetDialogOpen(false)}
                onConfirm={handleAddDatasetConfirm}
              />
            )}
          </Dialog>
        )}

        {/* Storage Limit Dialog */}
        {isStorageLimitDialogOpen && (
          <UnlockPremiumModal
            title="Storage Limit Reached"
            description="Your current plan has reached its dataset quota. To upload more or access premium datasets, please upgrade your plan."
            contactUsDesc="Contact your admin or reach out to our sales team to get started."
            emailSubject="Request to Add Quota"
            emailBody={`Hi Bvarta team,\n\nI would like to request an increase in my data upload quota.\n\nPlease share the details regarding the process.\n\nThanks!`}
            onClose={() => setIsStorageLimitDialogOpen(false)}
          />
        )}
      </div>
    </Dialog>
  );
}
